
// Usage (inside the footer "journey" box, which already has priceData + the button):
//
//   <StickyBookBar
//     :course-name="priceData.product.name"
//     :price="(hasBothVariants ? 'From ' : '') + $options.filters.priceFromLabel(headlinePriceData.pricesDiscounted)"
//     :original-price="$options.filters.priceFromLabel(headlinePriceData.product.prices)"
//     :tracking-context="{ experiment_id: '120_hour_pdp_test' }"
//   >
//     <template #cta>
//       <CoursesPurchaseButton :buttonclass="'compact'" :classroom="classroom" :text="'Book Now'" :bundle="bundle" />
//     </template>
//   </StickyBookBar>
//
// Add data-book-anchor to every real CTA (hero, mid-page block, this footer box).
//
// To lift the mobile chat/cookie widgets, add this to a GLOBAL stylesheet (not a
// scoped block — scoped styles can't reach other components), replacing the
// selectors with your actual widget containers:
//
//   @media (max-width: 639px) {
//     body.has-sticky-bar #your-chat-widget,
//     body.has-sticky-bar #your-cookie-widget {
//       bottom: calc(20px + var(--sticky-bar-offset, 0px)) !important; /* 20px = widget's normal bottom */
//       transition: bottom 0.28s ease;
//     }
//   }
//
// If a widget doesn't position by `bottom` (or you can't find its base value),
// lift it with a transform instead — no base value needed:
//   body.has-sticky-bar #your-chat-widget {
//     transform: translateY(calc(-1 * var(--sticky-bar-offset, 0px))) !important;
//     transition: transform 0.28s ease;
//   }
export default {
  name: 'StickyBookBar',

  props: {
    courseName: { type: String, required: true },
    // Pre-formatted current price, e.g. '£151.60' or 'From £151.60'.
    price: { type: String, required: true },
    // Optional struck-through original, e.g. '£379.00'.
    originalPrice: { type: String, default: '' },
    ctaLabel: { type: String, default: 'Book Now' },
    // Any element with this selector counts as an on-page CTA to hide behind.
    anchorSelector: { type: String, default: '[data-book-anchor]' },
    // Merged into the GTM pushes so bar events line up with your experiment data.
    trackingContext: { type: Object, default() { return {} } }
  },

  data() {
    return {
      isMounted: false,   // guards against showing during SSR / before observe
      hasAnchors: false,  // stays hidden if the page has no CTAs to track
      visibleCount: 0     // how many anchors are currently on screen
    }
  },

  computed: {
    // Reveal only once mounted, when we have anchors, and none are visible.
    show() {
      return this.isMounted && this.hasAnchors && this.visibleCount === 0
    }
  },

  watch: {
    // On show/hide: fire a one-time impression, nudge the corner widgets, and
    // let a parent react too (handy if a widget is your own component).
    show(val) {
      if (val && !this._impressionSent) {
        this._impressionSent = true
        this.track('sticky_bar_impression')
      }
      this.updateWidgetOffset(val)
      this.$emit('visibility-change', val)
    }
  },

  mounted() {
    this.isMounted = true
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
    this.observe()
  },

  beforeDestroy() {
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
    // Don't leave the widgets pushed up if we navigate away while shown.
    this.updateWidgetOffset(false)
  },

  methods: {
    // Find the page's CTAs and watch them. Safe to re-call if content swaps in.
    observe() {
      if (this._observer) this._observer.disconnect()

      const anchors = document.querySelectorAll(this.anchorSelector)
      if (!anchors || anchors.length === 0) {
        this.hasAnchors = false
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[StickyBookBar] no anchors matched "' + this.anchorSelector + '" — bar will stay hidden.')
        }
        return
      }

      this.hasAnchors = true

      // Track visible anchors in a Set so the count can't drift out of sync.
      const visible = new Set()
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.add(entry.target)
          } else {
            visible.delete(entry.target)
          }
        })
        this.visibleCount = visible.size
      }, { threshold: 0.01 })

      anchors.forEach((el) => this._observer.observe(el))
    },

    // Public: re-scan anchors after async content renders.
    refresh() {
      this.observe()
    },

    // Publish the bar's presence + height so global CSS can lift other widgets.
    // Height is measured live so widgets move by exactly the right amount.
    updateWidgetOffset(visible) {
      if (typeof document === 'undefined') return
      const root = document.documentElement
      if (visible) {
        const h = this.$el ? this.$el.offsetHeight : 64
        root.style.setProperty('--sticky-bar-offset', (h + 12) + 'px')
        document.body.classList.add('has-sticky-bar')
      } else {
        root.style.setProperty('--sticky-bar-offset', '0px')
        document.body.classList.remove('has-sticky-bar')
      }
    },

    // Fires for any click in the CTA area (slotted button or fallback).
    trackClick() {
      this.track('sticky_bar_book_click')
    },

    // Primary pipeline is GTM; falls back to raw dataLayer, then no-ops.
    track(eventName) {
      const payload = Object.assign({ event: eventName, course: this.courseName }, this.trackingContext)
      if (this.$gtm && typeof this.$gtm.push === 'function') {
        this.$gtm.push(payload)
      } else if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(payload)
      }
    }
  }
}
